/**
 * Keşif — «Yeniden tara»: bütçeyi yükselt ve devam et, ya da kopyala.
 *
 * İki eylem tek diyalogda, çünkü kullanıcının niyeti tek: *aynı hedeften daha
 * fazla veri*. Hangisinin mümkün olduğunu belirleyen şey kuyruk:
 *
 * - **Sürdür** — `maxPages` ÇEKMEYİ durdurur, keşfi durdurmaz. Bütçesi dolan
 *   bir tarama, bulup da çekmediği adresleri frontier'da `pending` bırakır
 *   (gerçek bir işte ölçüldü: 250 çekilmiş, 430 bekliyor). Sürdürmek bunları
 *   doğrudan tüketir; hiçbir sayfa yeniden indirilmez, veri kümesi yerinde
 *   büyür.
 * - **Kopyala** — kuyruk tükendiğinde sürdürülecek adres yoktur. O zaman
 *   dürüst olan tek şey yeni bir tarama; mevcut veri kümesi korunur.
 *
 * Kuyruk sayısı diyalog açılırken sunucudan okunur: liste kartındaki
 * `counters.pending` motorun bellek görünümüdür ve tamamlanmış bir işte
 * bayat olabilir — kullanıcıya "430 adres bekliyor" derken bunun ölçülmüş
 * olması gerekir.
 */
import { useEffect, useState } from "react";
import { Copy, Play } from "lucide-react";
import { getCrawlJob } from "../../transport/crawlClient";
import { useKesifStore } from "../../store/kesifStore";
import { Btn, ErrorBanner, Field, Modal, TextInput } from "../library/ui";
import type { CrawlJob } from "../../domain/kesif";

/** Sunucu tavanı (`MACDM_CRAWL_MAX_PAGES_PER_JOB`) ile aynı üst sınır. */
const MAX_BUDGET = 5000;

export function RecrawlModal({
  job,
  onClose,
  onCloned,
}: {
  job: CrawlJob;
  onClose: () => void;
  onCloned: (newJobId: string) => void;
}) {
  const extend = useKesifStore((s) => s.extend);
  const clone = useKesifStore((s) => s.clone);

  const fetched = job.counters.fetched;
  const [maxPages, setMaxPages] = useState(() =>
    Math.min(MAX_BUDGET, Math.max(fetched + 50, job.policy.maxPages * 2)),
  );
  const [maxDepth, setMaxDepth] = useState(job.policy.maxDepth);
  const [queued, setQueued] = useState<number | null>(null);
  const [busy, setBusy] = useState<"extend" | "clone" | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    void getCrawlJob(job.id)
      .then((d) => {
        if (alive) setQueued(d.frontier.pending + d.frontier.leased);
      })
      .catch(() => {
        // Kuyruk okunamadıysa «Sürdür»ü gizlemek yerine sunucunun karar
        // vermesine izin ver: uç boş kuyruğu zaten reddediyor.
        if (alive) setQueued(null);
      });
    return () => {
      alive = false;
    };
  }, [job.id]);

  const tooLow = maxPages <= fetched;
  const canExtend = queued === null || queued > 0;

  async function run(action: "extend" | "clone") {
    setBusy(action);
    setError(null);
    const patch = { maxPages, maxDepth };
    if (action === "extend") {
      const resumable = await extend(job.id, patch);
      setBusy(null);
      if (resumable === null) {
        setError(useKesifStore.getState().detailError ?? "Sürdürülemedi.");
        return;
      }
      onClose();
      return;
    }
    const id = await clone(job.id, patch);
    setBusy(null);
    if (id === null) {
      setError(useKesifStore.getState().listError ?? "Kopyalanamadı.");
      return;
    }
    onCloned(id);
  }

  return (
    <Modal
      open
      onClose={onClose}
      title="Yeniden tara"
      subtitle={`«${job.name || job.seedUrls[0]}» — şu ana kadar ${fetched.toLocaleString("tr-TR")} sayfa çekildi.`}
      width="max-w-2xl"
    >
      <div className="space-y-5">
        {error && <ErrorBanner message={error} onDismiss={() => setError(null)} />}

        <div className="grid gap-4 sm:grid-cols-2">
          <Field
            label="Sayfa bütçesi"
            hint={`Şu anki: ${job.policy.maxPages.toLocaleString("tr-TR")}. Bu sayı işin TOPLAM sayfa tavanıdır, tur başına değil.`}
          >
            <TextInput
              type="number"
              min={1}
              max={MAX_BUDGET}
              value={maxPages}
              onChange={(e) => setMaxPages(Number(e.target.value))}
              className={tooLow ? "border-rose-500/60 focus:border-rose-400" : ""}
            />
          </Field>
          <Field
            label="Derinlik"
            hint="Sayfa bütçesini artırmak derinlik sınırını aşmaz: daha derindeki sayfalar için bunu da yükseltmen gerekir."
          >
            <TextInput
              type="number"
              min={0}
              max={12}
              value={maxDepth}
              onChange={(e) => setMaxDepth(Number(e.target.value))}
            />
          </Field>
        </div>

        {tooLow && (
          <p className="text-[11.5px] text-rose-300">
            Bu iş zaten {fetched.toLocaleString("tr-TR")} sayfa çekti — yeni bütçe bundan
            büyük olmalı, aksi halde tarama ilk kontrolde durur.
          </p>
        )}

        <div className="space-y-2.5">
          <Option
            icon={<Play className="h-4 w-4" />}
            title="Kaldığı yerden sürdür"
            body={
              queued === null
                ? "Kuyruk okunuyor… Sürdürmek zaten indirilmiş sayfaları yeniden çekmez."
                : queued > 0
                  ? `${queued.toLocaleString("tr-TR")} adres kuyrukta bekliyor. Hiçbir sayfa yeniden indirilmez; veri kümesi bu işin içinde büyür.`
                  : "Bu taramanın kuyruğu tükendi — sürdürülecek adres yok. Aşağıdaki «Kopyala» ile aynı hedefi baştan tarayabilirsin."
            }
            action={
              <Btn
                tone="primary"
                size="sm"
                disabled={tooLow || !canExtend || busy !== null}
                loading={busy === "extend"}
                onClick={() => void run("extend")}
              >
                Sürdür
              </Btn>
            }
            dimmed={!canExtend}
          />
          <Option
            icon={<Copy className="h-4 w-4" />}
            title="Kopyala ve baştan tara"
            body="Aynı hedef ve aynı ayarlarla YENİ bir tarama açar; bu işin verisi olduğu gibi kalır. Yakın zamanda çekilmiş sayfalar önbellekten gelir."
            action={
              <Btn
                tone="outline"
                size="sm"
                disabled={busy !== null}
                loading={busy === "clone"}
                onClick={() => void run("clone")}
              >
                Kopyala
              </Btn>
            }
          />
        </div>
      </div>
    </Modal>
  );
}

function Option({
  icon,
  title,
  body,
  action,
  dimmed,
}: {
  icon: React.ReactNode;
  title: string;
  body: string;
  action: React.ReactNode;
  dimmed?: boolean;
}) {
  return (
    <div
      className={[
        "flex items-start gap-3 rounded-xl border border-white/[0.07] bg-white/[0.02] p-3.5",
        dimmed ? "opacity-60" : "",
      ].join(" ")}
    >
      <span className="mt-0.5 shrink-0 text-slate-400">{icon}</span>
      <div className="min-w-0 flex-1">
        <div className="text-[12.5px] font-medium text-white">{title}</div>
        <p className="mt-0.5 text-[11.5px] leading-relaxed text-slate-500">{body}</p>
      </div>
      <div className="shrink-0">{action}</div>
    </div>
  );
}
